<template>
  <form
    autocomplete="off"
    class="category-creation category-creation__form"
    v-on:submit.prevent="onSubmit">
    <input-text
      required="required"
      title="Название категории"
      v-bind:violations="nameErrors"
      v-model="name"
      v-letter-capital />
    <div>
      <label for="parentCategoryInput">Родительская категория:</label>
      <autocomplete-input
        name="parentCategoryInput"
        hide-details
        item-text="name"
        item-value="id"
        :menu-props="{ maxWidth: 586 }"
        v-bind:items="categoriesList"
        v-model="parentId"
      />
    </div>
    <div>
      <label for="parentCategoryInput">Прайс:</label>
      <autocomplete-input
        v-bind:disabled="isDisableDirectionInput"
        required="required"
        name="priceInput"
        hide-details
        item-text="name"
        item-value="id"
        :menu-props="{ maxWidth: 586 }"
        v-bind:items="itemsPriceList"
        v-model="priceId"
      />
    </div>
    <div class="category-creation__form__buttons">
      <button
        class="category-creation__form__button-cancel"
        type="button"
        v-on:click.prevent="close">
        Отмена
      </button>
      <button type="submit" class="category-creation__form__button-save">
        Сохранить
      </button>
    </div>
  </form>
</template>

<script>
import AutocompleteInput from '@/components/AutocompleteInput.vue';
import formMeaningfulFieldsMixin from '@/mixins/formMeaningfulFieldsMixin';
import InputText from './InputText.vue';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);
/**
 * @description Форма создания/редактирования категории услуги.
 * @type {any}
 */
export default {
  name: 'FormServiceCategoryCreation',
  components: { AutocompleteInput, InputText },
  computed: {
    nameErrors: resolveInputErrors('service.category.name'),
    /**
     * @description Список категорий для отображения.
     * @type {array}
     */
    categoriesList() {
      const categoryList = this
        .itemsPriceList?.find(
          (item) => item.id === this.priceId,
        )?.serviceCategories ?? [];
      return categoryList
        .filter(
          (item) => item.id !== this.initialId && !item.parentCategoryId,
        )
        .concat([{ id: 0, name: 'Нет', parentCategoryId: '' }]);
    },
    /**
     * @description Выключить выбор Прайса
     * @return {boolean}
     */
    isDisableDirectionInput() {
      return this.parentId !== 0;
    },
    /**
     * @description Значимые поля.
     * @type {any}
     */
    meaningfulFields: (vm) => ({
      name: vm.name,
      parentId: vm.parentId,
      priceId: vm.priceId,
    }),
  },
  data() {
    return {
      /**
       * @description Наименование.
       * @type {string}
       */
      name: this.initialName,
      /**
       * @description Идентификатор родительской категории.
       * @type {string}
       */
      parentId: this.initialParentCategoryId ?? 0,
      /**
       * @description Идентификатор Прайса.
       * @type {string}
       */
      priceId: this.initialPriceId,
      /**
       * @description Список папок для отображения.
       * @type {array}
       */
      itemsPriceList: this.priceList,
    };
  },
  methods: {
    /**
     * @description Закрыть форму.
     * @returns {void}
     */
    close() {
      this.$emit('close');
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      const request = {
        name: this.name,
        parent_category_id: this.parentId ? this.parentId : '',
        price_id: this.priceId ? this.priceId : '',
        order: this.initialOrder,
      };
      this.$emit('form-submit', request);
    },
  },
  mixins: [formMeaningfulFieldsMixin],
  props: {
    /**
     * @description Начальный Id клиники
     * @type {Number}
     */
    initialId: { type: Number, default: () => 0 },
    /**
     * @description Начальное имя
     * @type {String}
     */
    initialName: { type: String, default: () => '' },
    /**
     * @description Начальные данные Id родительской категории
     * @type {Number, String}
     */
    initialParentCategoryId: { type: [Number, String], default: () => 0 },
    /**
     * @description Начальные данные Id Прайса
     * @type {Number, String}
     */
    initialPriceId: { type: [Number, String], default: () => '' },
    /**
     * @description Нарушения ограничений.
     * @type {any}
     */
    inputErrors: {
      type: Object,
      required: true,
    },
    /**
     * @description Список категорий без прайса.
     * @type {any[]}
     */
    initialCategoryList: {
      required: true,
      type: Array,
    },
    /**
     * @description Порядок в очереди.
     * @type {Number}
     */
    initialOrder: {
      required: false,
      default: () => 0,
      type: Number,
    },
    /**
     * @description Список папок,
     * @type {any[]}
     */
    priceList: {
      required: true,
      type: Array,
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
.category-creation {
  position: relative;
  &__caption {
    font-weight: 600;
    font-size: 24px;
    line-height: 29px;
  }
  &__exit {
    position: absolute;
    right: -15px;
    top: 0px;
    background-image: url("@/assets/img/exit-black.svg");
    background-position: center;
    background-size: auto;
    background-repeat: no-repeat;
    background-color: #eaeff3;
    padding: 9px;
    border-radius: 2px;
    width: 32px;
    height: 32px;
    cursor: pointer;
    &:hover {
      background-color: transparent;
    }
  }
  &__form {
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 20px;
    margin: 20px 0 0 0;
    &__group {
      display: flex;
      flex-direction: column;
      gap: 10px;
      &.field_error {
        .category-creation__form__label {
          color: var(--warning-color);
        }
      }
    }
    &__label {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      color: #90a0b7;
      margin: 0 0 2px 0;
    }
    &__input {
      background: #ffffff;
      border: 1px solid #c2cfe0;
      border-radius: 4px;
      outline: none;
      height: 40px;
      width: 100%;
      padding: 0 10px;
      appearance: none;
      font-size: 16px;
      color: #192a3e;
      font-weight: 400;
      &:focus {
        color: #192a3e;
        background-color: #fff;
        border: 2px solid #0489e0;
        outline: 0;
      }
      &:disabled {
        background-color: #fff;
      }
    }
    &__select {
      background-color: #ffffff;
      background-image: url("@/assets/img/checked/checked-grey.svg");
      background-position: right 17px center;
      border: 1px solid #c2cfe0;
      border-radius: 4px;
      outline: none;
      height: 40px;
      padding: 0 10px;
      appearance: none;
      font-size: 16px;
      color: #192a3e;
      font-weight: 400;
      &:focus {
        color: #192a3e;
        background-color: #fff;
        border: 2px solid #0489e0;
        outline: 0;
        background-image: url("@/assets/img/checked/checked-grey-top.svg");
      }
      &:disabled {
        background-color: #fff;
        background-image: url("@/assets/img/checked/checked-grey.svg");
      }
    }
    &__buttons {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 10px;
      max-width: 438px;
      width: 100%;
      margin: 30px 0 0 auto;
    }
    &__button {
      &-cancel {
        @include buttons.btn-transparent(100%, 56px, 20px);
      }
      &-save {
        @include buttons.btn-blue(100%, 56px, 20px);
      }
    }
    &__error-text {
      color: var(--warning-color);
      font-size: 12px;
      line-height: 12px;
      padding: 0;
      margin: 0;
    }
  }
}
</style>
